import {
  getDownloadURL,
  listAll,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";

import { storage } from "../firebase/config";

export const CARD_IMAGE_TARGET_BYTES = 650 * 1024;
export const IMAGE_FILE_EXT_RE = /\.(png|jpe?g|gif|webp|svg)$/i;

const RASTER_IMAGE_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
]);

export const sanitizeStorageFileName = (name = "") =>
  String(name)
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

export const formatBytes = (bytes = 0) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export const parseFirebaseStoragePathFromUrl = (url = "") => {
  const value = String(url || "");
  const match = value.match(/\/o\/([^?]+)/);
  if (!match?.[1]) return "";

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

export const listStorageItemsRecursive = async (folderRef) => {
  const list = await listAll(folderRef);
  const nestedLists = await Promise.all(
    list.prefixes.map((prefixRef) => listStorageItemsRecursive(prefixRef)),
  );
  return [...list.items, ...nestedLists.flat()];
};

export const listImageLibraryItems = async (folderPath) => {
  const folder = storageRef(storage, folderPath);
  const itemRefs = await listStorageItemsRecursive(folder);
  const imageRefs = itemRefs.filter(
    (item) =>
      IMAGE_FILE_EXT_RE.test(item.name || "") ||
      IMAGE_FILE_EXT_RE.test(item.fullPath || ""),
  );

  const items = await Promise.all(
    imageRefs.map(async (itemRef) => {
      const url = await getDownloadURL(itemRef);
      return {
        name: itemRef.name,
        fullPath: itemRef.fullPath,
        url,
      };
    }),
  );

  return items.sort((a, b) => a.fullPath.localeCompare(b.fullPath));
};

const blobFromCanvas = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to encode image."));
          return;
        }
        resolve(blob);
      },
      type,
      quality,
    );
  });

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load selected image."));
    };
    image.src = objectUrl;
  });

const replaceExtensionWithWebp = (name = "") => {
  const safeName = sanitizeStorageFileName(name) || `card_image_${Date.now()}`;
  const withoutExt = safeName.replace(/\.[^.]+$/, "");
  return `${withoutExt}.webp`;
};

export const optimizeCardImageFile = async (
  file,
  { targetBytes = CARD_IMAGE_TARGET_BYTES } = {},
) => {
  const originalSize = Number(file?.size || 0);
  const originalType = String(file?.type || "").toLowerCase();
  const originalName = file?.name || `card_image_${Date.now()}`;

  const baseResult = {
    file,
    originalSize,
    finalSize: originalSize,
    compressed: false,
    warning: "",
  };

  if (!file || originalSize <= targetBytes) return baseResult;

  if (!RASTER_IMAGE_TYPES.has(originalType)) {
    return {
      ...baseResult,
      warning: `${originalName} is ${formatBytes(
        originalSize,
      )}. GIF/SVG files are uploaded unchanged.`,
    };
  }

  const image = await loadImageFromFile(file);
  const naturalWidth = image.naturalWidth || image.width;
  const naturalHeight = image.naturalHeight || image.height;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const qualities = [0.86, 0.78, 0.7, 0.62, 0.54, 0.46, 0.38];
  let bestBlob = null;
  let bestSize = Number.POSITIVE_INFINITY;
  let scale = 1;

  for (let dimensionPass = 0; dimensionPass < 8; dimensionPass += 1) {
    const width = Math.max(1, Math.round(naturalWidth * scale));
    const height = Math.max(1, Math.round(naturalHeight * scale));

    canvas.width = width;
    canvas.height = height;
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(image, 0, 0, width, height);

    for (const quality of qualities) {
      const blob = await blobFromCanvas(canvas, "image/webp", quality);
      if (blob.size < bestSize) {
        bestBlob = blob;
        bestSize = blob.size;
      }
      if (blob.size <= targetBytes) {
        const optimizedFile = new File([blob], replaceExtensionWithWebp(originalName), {
          type: "image/webp",
          lastModified: Date.now(),
        });
        return {
          file: optimizedFile,
          originalSize,
          finalSize: blob.size,
          compressed: true,
          warning: "",
        };
      }
    }

    scale *= 0.85;
  }

  if (!bestBlob) return baseResult;

  if (bestBlob.size >= originalSize) {
    return {
      ...baseResult,
      warning: `${originalName} could not be compressed smaller than the original ${formatBytes(
        originalSize,
      )}; uploading the original file.`,
    };
  }

  const optimizedFile = new File([bestBlob], replaceExtensionWithWebp(originalName), {
    type: "image/webp",
    lastModified: Date.now(),
  });

  return {
    file: optimizedFile,
    originalSize,
    finalSize: bestBlob.size,
    compressed: true,
    warning:
      bestBlob.size > targetBytes
        ? `${originalName} was optimized from ${formatBytes(
            originalSize,
          )} to ${formatBytes(bestBlob.size)}, but is still above ${formatBytes(
            targetBytes,
          )}.`
        : "",
  };
};

export const uploadCardImage = async ({
  file,
  folderPath,
  targetBytes = CARD_IMAGE_TARGET_BYTES,
}) => {
  const optimized = await optimizeCardImageFile(file, { targetBytes });
  const safeName =
    sanitizeStorageFileName(optimized.file?.name) || `card_image_${Date.now()}`;
  const uploadPath = `${folderPath}/${Date.now()}_${safeName}`;
  const fileRef = storageRef(storage, uploadPath);

  await uploadBytes(fileRef, optimized.file, {
    contentType: optimized.file?.type || file?.type || "application/octet-stream",
    customMetadata: {
      originalFileName: String(file?.name || ""),
      originalSizeBytes: String(optimized.originalSize || 0),
      finalSizeBytes: String(optimized.finalSize || optimized.file?.size || 0),
      compressed: String(!!optimized.compressed),
    },
  });

  const url = await getDownloadURL(fileRef);
  return {
    url,
    name: safeName,
    fullPath: uploadPath,
    originalSize: optimized.originalSize,
    finalSize: optimized.finalSize || optimized.file?.size || 0,
    compressed: optimized.compressed,
    warning: optimized.warning,
  };
};
